import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

const DEFAULT_TIMEOUT_MIN = 3;
const COUNTDOWN_SEC       = 60;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {

  private idleTimer: any   = null;
  private warnTimer: any   = null;
  private countdownTimer: any = null;
  private warningEl: HTMLElement | null = null;
  private countdownSec = COUNTDOWN_SEC;
  private active = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  start(): void {
    if (this.active) return;
    this.active = true;
    this.ngZone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach(ev =>
        document.addEventListener(ev, this._onActivity, { passive: true })
      );
    });
    this._reset();
  }

  stop(): void {
    this.active = false;
    ACTIVITY_EVENTS.forEach(ev =>
      document.removeEventListener(ev, this._onActivity)
    );
    this._clearTimers();
    this._removeWarning();
  }

  ngOnDestroy(): void { this.stop(); }


  private _onActivity = (): void => {
    if (!this.active) return;
    this._reset();
  };

  private _timeoutMinutes(): number {
    const v = Number(this.authService.getCurrentUser()?.sessionTimeoutMinutes);
    return (v && v > 0) ? v : DEFAULT_TIMEOUT_MIN;
  }

  private _reset(): void {
    this._clearTimers();
    this._removeWarning();

    const idleMs = this._timeoutMinutes() * 60 * 1000;
    const warnMs = Math.max(idleMs - COUNTDOWN_SEC * 1000, 5000);
    this.countdownSec = Math.min(COUNTDOWN_SEC, Math.round(idleMs / 1000));

    this.warnTimer = setTimeout(() => {
      this.ngZone.run(() => this._showWarning());
    }, warnMs);

    this.idleTimer = setTimeout(() => {
      this.ngZone.run(() => this._logout());
    }, idleMs);
  }

  private _clearTimers(): void {
    clearTimeout(this.idleTimer);
    clearTimeout(this.warnTimer);
    clearInterval(this.countdownTimer);
    this.idleTimer = this.warnTimer = this.countdownTimer = null;
  }

  private _injectStyles(): void {
    if (document.getElementById('inactivity-styles')) return;
    const style = document.createElement('style');
    style.id = 'inactivity-styles';
    style.textContent = `
      @keyframes inact-slide-down {
        from { opacity:0; transform:translateX(-50%) translateY(-18px); }
        to   { opacity:1; transform:translateX(-50%) translateY(0); }
      }
      #inactivity-popup button:focus-visible {
        outline:2px solid #334155; outline-offset:2px;
      }
      @media (prefers-reduced-motion: reduce) {
        #inactivity-popup { animation:none !important; }
        #inact-bar { transition:none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  private _showWarning(): void {
    this._removeWarning();
    this._injectStyles();

    const popup = document.createElement('div');
    popup.id = 'inactivity-popup';
    popup.setAttribute('role', 'alertdialog');
    popup.setAttribute('aria-live', 'assertive');
    popup.setAttribute('aria-label', 'Session expiring soon');
    popup.style.cssText = `
      position:fixed; top:22px; left:50%; transform:translateX(-50%);
      z-index:100000; width:340px; max-width:calc(100vw - 32px);
      font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
      animation:inact-slide-down .28s cubic-bezier(.16,1,.3,1);
    `;

    popup.innerHTML = `
      <div style="
        background:rgba(255,255,255,.62);
        -webkit-backdrop-filter:blur(22px) saturate(180%);
        backdrop-filter:blur(22px) saturate(180%);
        border:1px solid rgba(255,255,255,.6);
        border-radius:14px;
        box-shadow:0 10px 36px rgba(24,8,10,.20), inset 0 1px 0 rgba(255,255,255,.5);
        padding:14px 16px 13px; overflow:hidden;">

        <div style="display:flex; align-items:center; gap:11px;">
          <div style="
            width:34px; height:34px; border-radius:10px; flex-shrink:0;
            background:rgba(51,65,85,.10);
            display:flex; align-items:center; justify-content:center;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke="#334155" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>
            </svg>
          </div>
          <div style="flex:1; min-width:0;">
            <div style="font-size:14px; font-weight:700; color:#1a1014; line-height:1.2;">Session expiring</div>
            <div style="font-size:12px; color:#7a6a6e; margin-top:2px;">You've been inactive for a while</div>
          </div>
          <div id="inactivity-countdown" aria-live="off" style="
            flex-shrink:0; font-size:15px; font-weight:800; color:#334155;
            font-variant-numeric:tabular-nums; background:rgba(51,65,85,.08);
            padding:5px 9px; border-radius:8px; letter-spacing:.5px;">0:${COUNTDOWN_SEC}</div>
        </div>

        <div style="display:flex; gap:8px; margin-top:13px;">
          <button id="inact-stay" type="button" style="
            flex:1; height:36px; border:none; border-radius:9px;
            background:#334155; color:#fff; font-size:13px; font-weight:600;
            cursor:pointer; transition:background .18s; touch-action:manipulation;
            font-family:inherit;">Stay logged in</button>
          <button id="inact-logout" type="button" style="
            height:36px; padding:0 14px; border:1px solid rgba(51,65,85,.20);
            border-radius:9px; background:transparent; color:#334155;
            font-size:13px; font-weight:600; cursor:pointer; transition:all .18s;
            touch-action:manipulation; font-family:inherit;">Log out</button>
        </div>

        <div style="height:3px; border-radius:99px; background:rgba(51,65,85,.10); margin-top:12px; overflow:hidden;">
          <div id="inact-bar" style="
            height:100%; width:100%; background:#334155; border-radius:99px;
            transform-origin:left; transform:scaleX(1); transition:transform 1s linear;"></div>
        </div>
      </div>
    `;

    const stayBtn   = popup.querySelector('#inact-stay')   as HTMLButtonElement;
    const logoutBtn = popup.querySelector('#inact-logout') as HTMLButtonElement;

    stayBtn.addEventListener('mouseenter', () => stayBtn.style.background = '#8b0010');
    stayBtn.addEventListener('mouseleave', () => stayBtn.style.background = '#334155');
    stayBtn.addEventListener('click', () => this._reset());

    logoutBtn.addEventListener('mouseenter', () => logoutBtn.style.background = 'rgba(51,65,85,.07)');
    logoutBtn.addEventListener('mouseleave', () => logoutBtn.style.background = 'transparent');
    logoutBtn.addEventListener('click', () => this._logout());

    document.body.appendChild(popup);
    this.warningEl = popup;
    stayBtn.focus();

    this.countdownTimer = setInterval(() => {
      this.ngZone.run(() => {
        this.countdownSec--;
        const left = Math.max(0, this.countdownSec);
        const el  = document.getElementById('inactivity-countdown');
        const bar = document.getElementById('inact-bar');
        if (el)  el.textContent = `0:${String(left).padStart(2, '0')}`;
        if (bar) bar.style.transform = `scaleX(${left / COUNTDOWN_SEC})`;
        if (this.countdownSec <= 0) this._logout();
      });
    }, 1000);
  }

  private _removeWarning(): void {
    clearInterval(this.countdownTimer);
    this.countdownTimer = null;
    if (this.warningEl) {
      this.warningEl.remove();
      this.warningEl = null;
    }
  }

  private _logout(): void {
    this.stop();
    this.authService.logout();
  }
}
